import { useLocalization } from "@progress/kendo-react-intl";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { AccessToken } from "../../../config/AccessToken";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { ListView, ListViewFooter, ListViewItemProps } from "@progress/kendo-react-listview";
import { SvgIcon } from "@progress/kendo-react-common";
import { Avatar } from "@progress/kendo-react-layout";
import { checkboxIcon, saveIcon, searchIcon, starIcon } from "@progress/kendo-svg-icons";
import { TextBox, TextBoxChangeEvent, TextBoxProps } from "@progress/kendo-react-inputs";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { loadingPanel } from "../../LoadingPanel";
import { NotificationGroup, Notification } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";
import { Grid, GridColumn, GridCustomCellProps } from "@progress/kendo-react-grid";
import { useOutletContext } from "react-router-dom";




/**
 * ConfigDevicesBinding is a React component that manages the binding of devices
 * within a list view. It handles the fetching and displaying of devices,
 * device bindings, and groups, as well as user interactions such as device
 * selection, filtering, and updating device information. The component
 * communicates with a server for data retrieval and updating using access tokens
 * for authorization. It also provides a user interface for managing device
 * bindings, including a search feature and save functionality.
 */

export const ConfigDevicesBinding = () => {

    const outletContext = useOutletContext<{ drawerExpanded?: boolean } | null>();
    const drawerExpanded = Boolean(outletContext?.drawerExpanded);

    type Device = {
        mac: string;
        name?: string;
        deviceTypeName?: string;
        battery?: string | number;
        messages?: number;
        [key: string]: any;
    };

    type DeviceBinding = {
        mac?: string;
        name?: string;
        serial?: string;
        startDate?: string;
        endDate?: string;
        recent?: string;
        [key: string]: any;
    };

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const COLORS = {
        primary: '#018CA5',
        accent: '#198793',
        selectedBg: '#198792',
        panelBg: '#F7F7F7',
        title: '#1A8893',
        white: '#ffffff',
        markerRed: '#FF0000',
        borderMuted: '#ccc'
    };


    const [devicesList, setDevicesList] = useState<Device[]>([]);
    const [counterActiveDevices, setCounterActiveDevices] = useState(0);
    //const [counterInactiveDevices, setCounterInactiveDevices] = useState(null);
    const [deviceToDelete, setDeviceToDelete] = useState<any>(null);
    const [deviceInfoSelected, setDeviceInfoSelected] = useState<Device | null>(null);
    const [deviceMacSelected, setDeviceMacSelected] = useState('');
    const [deviceTypeName, setDeviceTypeName] = useState('');
    const [deviceNameSelected, setDeviceNameSelected] = useState<TextBoxProps["value"]>('');
    const [searchFilterValue, setSearchFilterValue] = useState('');

    const [listViewData, setListViewData] = useState<Device[]>([]);

    const [devicesBinding, setDevicesBinding] = useState<DeviceBinding[]>([]);
    const [lastFromBinding, setLastFromBinding] = useState('');
    const [lastToBinding, setLastToBinding] = useState('');
    const [closedBind, setClosedBind] = useState<boolean>(false);


    const [visible, setVisible] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [updatedMessage, setUpdatedMessage] = useState(false);


    const localizationService = useLocalization();

    const [instance, accessTokenRequest] = AccessToken();

    const languageId = localStorage.getItem('languageId');

    const userType = localStorage.getItem('userType');

    const [loading, setLoading] = useState(false);
    const devicesListContainerRef = useRef<HTMLDivElement | null>(null);
    const listScrollTopRef = useRef(0);

    const getListScrollElement = () => {
        const container = devicesListContainerRef.current;
        if (!container) {
            return null;
        }

        return (container.querySelector('.k-listview-content') as HTMLElement | null) ?? container;
    };

    const rememberListScrollPosition = () => {
        const scrollElement = getListScrollElement();
        if (!scrollElement) {
            return;
        }

        listScrollTopRef.current = scrollElement.scrollTop;
    };

    const restoreListScrollPosition = () => {
        const savedScrollTop = listScrollTopRef.current;

        const applyScroll = () => {
            const currentScrollElement = getListScrollElement();
            if (!currentScrollElement) {
                return;
            }

            currentScrollElement.scrollTop = savedScrollTop;
        };

        applyScroll();
        requestAnimationFrame(() => {
            applyScroll();

            // First selection also changes layout; apply twice to avoid initial jump-to-top.
            requestAnimationFrame(() => {
                applyScroll();
            });
        });
    };

    const withAccessToken = async (operation: (accessToken: string) => Promise<void> | void) => {
        const storedAccessToken = localStorage.getItem('accessToken');
        if (storedAccessToken) {
            await operation(storedAccessToken);
            return;
        }

        try {
            await instance.initialize();
            const accessTokenResponse: any = await instance.acquireTokenSilent(accessTokenRequest);
            await operation(accessTokenResponse.accessToken);
        } catch (error: any) {
            if (error instanceof InteractionRequiredAuthError) {
                instance.acquireTokenRedirect(accessTokenRequest);
            }
            console.log(error);
        }
    };

    const getDeviceList = async () => {

        setLoading(true);

        const commonOperations = async (accessToken: string) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            try {
                const res: any = await axios.get(`${SERVER_URL}/device/list`, { headers });

                if (!res?.data?.data?.length) {
                    setDevicesList([]);
                    setCounterActiveDevices(0);
                    return;
                }

                const data: Device[] = res?.data?.data ?? [];
                setCounterActiveDevices(data.length);
                setDevicesList(data);
            } catch (error: any) {
                setErrorMessage(error?.message ?? 'Unexpected error while loading devices');
            } finally {
                setLoading(false);
            }
        };

        await withAccessToken(commonOperations);
    }

    //  call API on page load
    useEffect(() => {
        getDeviceList()
    }, [])

    const hasAppliedDefaultSelectionRef = useRef(false);

    useEffect(() => {
        if (hasAppliedDefaultSelectionRef.current) {
            return;
        }

        if (deviceMacSelected !== '') {
            hasAppliedDefaultSelectionRef.current = true;
            return;
        }

        if (!Array.isArray(devicesList) || devicesList.length === 0) {
            return;
        }

        hasAppliedDefaultSelectionRef.current = true;
        getDeviceInfoOnClick(devicesList[0]);
    }, [devicesList, deviceMacSelected]);

    const convertToLocaleString = (dateStr: string): string => {
        const [day, month, yearAndTime] = dateStr.split('/');
        const [year, time] = yearAndTime.split(' ');

        const isoString = `${year}-${month}-${day}T${time}Z`;
        const date = new Date(isoString);

        return date.toLocaleString('it-IT', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });
    };


    const getDevicesBindingList = async (deviceMac: string) => {

        const commonOperations = async (accessToken: string) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            try {
                const res: any = await axios.get(`${SERVER_URL}/device/bindings/${deviceMac}`, { headers });
                const rawData: DeviceBinding[] = res?.data?.data ?? [];

                if (rawData.length === 0) {
                    setDevicesBinding([]);
                    setLastFromBinding('');
                    setLastToBinding('');
                    setClosedBind(false);
                    return;
                }

                const data = rawData.map((binding: DeviceBinding) => {
                    const startDateValue = binding?.startDate == null
                        ? localizationService.toLanguageString('binding.date_not_found', 'NOT_FOUND')
                        : convertToLocaleString(binding.startDate.split('T')[0]).replace(', ', ' ');

                    const endDateValue = binding?.endDate == null
                        ? localizationService.toLanguageString('binding.date_not_found', 'NOT_FOUND')
                        : convertToLocaleString(binding.endDate.split('T')[0]).replace(', ', ' ');

                    return {
                        ...binding,
                        startDate: startDateValue,
                        endDate: endDateValue
                    };
                });

                const firstEndDate = data[0]?.endDate ?? '';
                setClosedBind(!(firstEndDate.includes('9999-12-31') || firstEndDate.includes('31/12/9999')));

                data[0].recent = 'recent';
                setDevicesBinding(data);
                setLastFromBinding(data[0]?.startDate ?? '');
                setLastToBinding(data[0]?.endDate ?? '');
            } catch (error: any) {
                setErrorMessage(error?.message ?? 'Unexpected error while loading bindings');
            }
        };

        await withAccessToken(commonOperations);
    }

    const applyDeviceSearchFilter = (value: string, sourceList: any[] = []) => {
        if (!value) {
            setListViewData(sourceList);
            return;
        }

        const normalizedValue = value.toLocaleLowerCase();
        const newData = sourceList.filter((device: any) => {
            let match = false;
            for (const property in device) {
                if ((device['name']?.toString() && device['name']?.toString().toLocaleLowerCase().indexOf(normalizedValue) >= 0) || (device['mac']?.toString() && device['mac']?.toString().toLocaleLowerCase().indexOf(normalizedValue) >= 0)) {
                    match = true;
                }
            }
            return match;
        });

        setListViewData(newData);
    };

    const onFilterChange = (ev: any) => {
        const value = ev.value ?? '';
        setSearchFilterValue(value);
        applyDeviceSearchFilter(value, devicesList ?? []);
    };

    const MyFooter = () => {
        return (
            <ListViewFooter
                style={{ color: COLORS.primary, fontSize: 14 }}
                className="pl-3 pb-2 pt-2"
            >
                {/*{counterActiveDevices} {localizationService.toLanguageString('configuration.devices.active_devices', 'NOT_FOUND')}*/}
            </ListViewFooter>
        );
    };

    const toggleDialog = (item?: any) => {
        setDeviceToDelete(item);
        setVisible(!visible);
    };

    const deleteDevice = () => {
        console.log(deviceToDelete);
    }

    // called on click device
    const getDeviceInfoOnClick = (item: any) => {
        rememberListScrollPosition();
        setDeviceInfoSelected(item)
        setDeviceMacSelected(item.mac);
        setDeviceNameSelected(item.name);
        setDeviceTypeName(item.deviceTypeName);
        getGroupList();
        getDevicesBindingList(item.mac)
        //setDeviceVersionSelected(item.deviceVersion); // if exist...TBD
    }

    useEffect(() => {
        if (deviceMacSelected) {
            restoreListScrollPosition();
        }
    }, [deviceMacSelected]);

    const DevicesRender = (props: ListViewItemProps, index: number) => {
        const item = props.dataItem as Device;
        const isSelected = deviceMacSelected === item.mac;
        return (
            <div
                key={index}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '10px',
                    gap: '8px',
                    //paddingRight: '10px',
                    cursor: `${userType === 'StandardOnlyLocator' || userType === 'UserAdminOnlyLocator' ? 'default' : 'pointer'}`,
                }}
            >
                <p
                    className="devices-binding-name-item"
                    ref={(el) => (itemRefs.current[item.id] = el)}
                    style={{
                        cursor: 'pointer',
                        margin: '0px',
                        padding: '10px',
                        flex: 1,
                        whiteSpace: 'nowrap',
                        float: 'left',
                        alignContent: 'center',
                        fontSize: `${deviceMacSelected != '' ? 'clamp(10px, 0.8vw, 16px)' : 'clamp(9px, 0.7vw, 16px)'}`,
                        //pointerEvents: `${userType === 'StandardOnlyLocator' ? 'none' : 'auto'}`,
                        //opacity: `${userType === 'StandardOnlyLocator' ? 0.5 : 1}`,
                        background: isSelected ? COLORS.selectedBg : COLORS.white,
                        color: !isSelected ? COLORS.selectedBg : COLORS.white
                    }}
                    onClick={(e) => {
                        getDeviceInfoOnClick(item);
                        //const title = e.currentTarget.querySelector('.device-binding-name-h2') as HTMLElement | null;
                        //title?.focus({ preventScroll: true });
                    }}
                >
                    {item.name} ({item.mac})
                </p>
            </div>
        );
    };

    const handleChangeDeviceName = useCallback((event: TextBoxChangeEvent) => {
        setDeviceNameSelected(event.target.value);
    }, []);

    const getGroupList = async () => {

        setLoading(true);

        const commonOperations = async (accessToken: string) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            try {
                await axios.get(`${SERVER_URL}/group/list`, { headers });
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
            }
        }

        await withAccessToken(commonOperations);
    }

    const CustomCell = (props: GridCustomCellProps) => {
        const tdProps = props.tdProps ?? {};
        return (
            <td
                {...tdProps}
                style={{
                    ...(tdProps.style ?? {}),
                    fontSize: '14px',
                }}
            >
                {props.children}
            </td>
        );

    };

    const formatDate = (date: any) => {
        const pad = (n: any) => n.toString().padStart(2, '0');

        const day = pad(date.getUTCDate());
        const month = pad(date.getUTCMonth() + 1); // i mesi partono da 0
        const year = date.getUTCFullYear();

        const hours = pad(date.getUTCHours());
        const minutes = pad(date.getUTCMinutes());
        const seconds = pad(date.getUTCSeconds());

        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    }

    const saveDevice = async (deviceMac: string, deviceName: TextBoxProps["value"]) => {

        setLoading(true);

        const currentStartDate = new Date()

        const commonOperations = async (accessToken: string) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            const params = {
                deviceMac,
                deviceName,
                startDate: formatDate(currentStartDate)
            };

            try {
                await axios.post(`${SERVER_URL}/device/binding/update`, params, { headers })

                setDeviceNameSelected('')
                setUpdatedMessage(true);
                setClosedBind(true);

                getDevicesBindingList(deviceMac);
                getDeviceList();

                // Scroll to top
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                    document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
                }
                setLoading(false)

                if (!updatedMessage) {
                    setTimeout(() => {
                        setUpdatedMessage(false);
                    }, 3000);
                }
            } catch (error: any) {
                setErrorMessage(error?.response?.data?.errorMessage ?? error?.message ?? 'Unexpected error while saving device');
                setLoading(false)
            }
        }

        await withAccessToken(commonOperations);
    }

    const handleCloseBinding = (deviceSelected: DeviceBinding) => {

        devicesBinding.forEach((device: DeviceBinding) => {
            if (device.startDate === deviceSelected.startDate) {
                device.endDate = new Date().toISOString();
            }
        })
        setDevicesBinding([...devicesBinding]);
        setDeviceNameSelected('');
        closeDeviceBinding(deviceSelected, true)
    }

    const closeDeviceBinding = async (deviceSelected: DeviceBinding, onlyClose?: boolean) => {

        setLoading(true);

        const newDevice: DeviceBinding = JSON.parse(JSON.stringify(deviceSelected))

        const endDateFormatted = new Date()

        const data = {
            deviceMac: newDevice.mac,
            deviceName: newDevice.name ?? deviceNameSelected,
            startDate: newDevice.startDate,
            endDate: formatDate(endDateFormatted)
        };

        const commonOperations = async (accessToken: string) => {

            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            try {
                await axios.delete(`${SERVER_URL}/device/binding/end`, { data, headers })

                setLastToBinding('');
                setLastFromBinding('');
                setClosedBind(true);

                if (lastToBinding === '31/12/9999 00:00:00' && !onlyClose) {
                    await saveDevice(deviceMacSelected, deviceNameSelected)
                } else {
                    getDevicesBindingList(newDevice.mac ?? '')
                    getGroupList();
                    getDeviceList();
                }

                setLoading(false);
            } catch (error: any) {
                setLoading(false);
                setErrorMessage(error?.message ?? 'Unexpected error while closing binding');
            };
        }

        await withAccessToken(commonOperations);
    }

    const CloseBindingIconGrid = (propsGrid: GridCustomCellProps) => {
        const deviceSelected = propsGrid?.dataItem
        const tdProps = propsGrid.tdProps ?? {};
        const shouldShowClose = deviceSelected != null && deviceSelected?.recent == 'recent' && !closedBind;

        return (
            <td
                {...tdProps}
                style={{
                    ...(tdProps.style ?? {}),
                    textAlign: 'center'
                }}
            >
                {shouldShowClose && (
                    <span
                        className="k-icon k-font-icon k-i-unlink"
                        style={{ zoom: '1.5', cursor: 'pointer', color: '#018CA5' }}
                        onClick={() => handleCloseBinding(deviceSelected)}
                        title={localizationService.toLanguageString('configuration.devices.close_binding', 'NOT FOUND')}
                    />
                )}
            </td>
        )
    };

    const itemRefs: any = useRef([]);

    const devicesBindingCss = `
        .devices-bind-root {
            display: flex;
            gap: 12px;
            overflow: auto;
            height: 100%;
            min-height: 0;
            align-items: stretch;
            width: 100%;
            padding: 10px;
            box-sizing: border-box;
        }

        .devices-bind-root.no-selection .devices-bind-left {
            flex: 1.5 1 0px;
        }

        .devices-bind-root.has-selection {
            flex-direction: row;
        }

        .devices-bind-root.has-selection .devices-bind-left {
            flex: 1.5 1 0px;
        }

        .devices-bind-root.has-selection .devices-bind-right {
            flex: 1 1 0;
            min-width: 0;
        }

        .devices-bind-left {
            flex: 1.5 1 0px;
            border-radius: 14px;
            overflow-y: hidden;
            overflow-x: hidden;
            height: 100%;
            min-height: 0;
            display: flex;
            flex-direction: column;
            padding: 10px;
            background: #F7F7F7;
            box-sizing: border-box;
            border: 1px solid #d6e8ee;
            box-shadow: 0 10px 24px rgba(10, 55, 75, 0.08);
        }

        .devices-bind-title {
            font-weight: 900;
            letter-spacing: 0.55px;
            text-shadow: 0 1px 0 rgba(255, 255, 255, 0.7);
            margin: 0;
        }

        .devices-bind-left-content {
            flex: 1;
            min-height: 0;
            padding: 8px;
            overflow-y: auto;
            width: 100%;
            display: flex;
            flex-direction: column;
        }

        .devices-bind-toolbar {
            display: flex;
            gap: 8px;
            align-items: center;
        }

        .devices-bind-toolbar .k-textbox {
            flex: 1;
            min-width: 180px;
        }

        .devices-bind-list {
            margin-top: 4px;
            flex: 1 1 auto;
            min-height: 0;
        }

        .devices-bind-left .k-listview {
            height: 94%;
            overflow-y: auto;
        }

        .devices-bind-left .k-listview-content {
            overflow-x: hidden;
        }

        .devices-bind-device-row {
            background: #ffffff;
            border: 1px solid #cfe3ea;
            border-radius: 12px;
            padding: 6px 8px;
            box-shadow: 0 3px 10px rgba(10, 55, 75, 0.06);
            transition: transform 120ms ease, box-shadow 180ms ease;
        }

        .devices-bind-device-row:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 18px rgba(10, 55, 75, 0.12);
        }

        .devices-binding-name-item{
            background: #ffffff;
            border: 1px solid #cfe3ea;
            border-radius: 12px;
            padding: 6px 8px;
            box-shadow: 0 3px 10px rgba(10, 55, 75, 0.06);
            transition: transform 120ms ease, box-shadow 180ms ease;
        }

        .devices-binding-name:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 18px rgba(10, 55, 75, 0.12);
        }

        .devices-bind-right {
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            flex-grow: 2;
            align-items: stretch;
            border: 1px solid #cfe3ea;
            box-shadow: rgba(0, 0, 0, 0.08) 0px 6px 18px;
            border-radius: 12px;
            padding: 12px;
            min-width: 0;
            height: 100%;
        }

        .devices-bind-toolbar-row {
            display: block;
            width: 100%;
            margin: 0;
            background: #F7F7F7;
        }

        .devices-bind-right .k-listview-item {
            width: 100%;
        }

        .devices-bind-right fieldset {
            border: 1px solid #d4e3f1;
            border-radius: 12px;
            padding: 12px;
            background: #ffffff;
        }

        .devices-bind-grid {
            border-radius: 12px;
            overflow: hidden;
            border: 1px solid #d4e3f1;
            background: #ffffff;
            width: 100%;
            max-width: 100%;
        }

        .devices-bind-grid table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            min-width: 590px;
            table-layout: fixed;
        }

        .devices-bind-grid thead {
            background: #f2f8fc;
        }

        .devices-bind-grid th,
        .devices-bind-grid td {
            font-size: 14px;
            padding: 10px 12px;
            border-bottom: 1px solid #e6eef3;
            text-align: left;
            white-space: nowrap;
        }

        .devices-bind-grid th:last-child,
        .devices-bind-grid td:last-child {
            text-align: center;
        }

        .devices-bind-grid thead th:first-child {
            border-top-left-radius: 12px;
        }

        .devices-bind-grid thead th:last-child {
            border-top-right-radius: 12px;
        }

        .devices-bind-grid tbody tr:last-child td {
            border-bottom: none;
        }

        .devices-bind-grid-wrapper {
            margin: .5rem;
            overflow-x: auto;
            border-radius: 12px;
        }

        .devices-bind-save-row {
            justify-content: flex-end;
        }

        .devices-bind-root .k-button {
            transition: transform 120ms ease, box-shadow 180ms ease;
        }

        .devices-bind-root .k-button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 8px 16px rgba(15, 50, 80, 0.14);
        }

        @media (max-width: 1100px) {
            .devices-bind-root {
                flex-direction: column;
                height: calc(100vh - 140px);
            }

            .devices-bind-left {
                width: 100%;
                flex-basis: auto;
                height: 50%;
            }

            .devices-bind-right {
                width: 100%;
                height: 58%;
            }

            .devices-bind-root.no-selection .devices-bind-left {
                height: 100%;
            }
        }

        @media (max-width: 760px) {
            .devices-bind-toolbar {
                flex-wrap: wrap;
            }

            .devices-bind-left,
            .devices-bind-right {
                border-radius: 10px;
            }

            .devices-bind-left {
                padding: 10px;
            }

            .devices-bind-right .k-listview-item {
                max-width: 100%;
            }

            .devices-bind-grid {
                max-width: 100%;
            }
        }
    `;

    useEffect(() => {
        applyDeviceSearchFilter(searchFilterValue, devicesList ?? []);
    }, [devicesList, searchFilterValue]);

    const scrollContainerRef = useRef<HTMLDivElement>(null);


    return (
        <>
            <style>{devicesBindingCss}</style>

            {loading && loadingPanel}
            {devicesList.length === 0 &&
                // Mostra un messaggio o un altro contenuto se non ci sono dati
                <div style={{ textAlign: 'center', padding: '20px', border: `1px dashed ${COLORS.borderMuted}` }}>
                    <p>{localizationService.toLanguageString('configuration.devices.no_data', 'NOT_FOUND')}</p>
                </div>
            }
            <NotificationGroup
                style={{
                    right: 20,
                    bottom: 40,
                    position: "fixed",
                    alignItems: "flex-start",
                    flexWrap: "wrap-reverse",
                    zIndex: 1000
                }}
            >
                <Fade enter={true} exit={true}>
                    {errorMessage && (
                        <Notification
                            type={{ style: "error", icon: true }}
                            onClose={() => setErrorMessage('')}
                        >
                            <span>{errorMessage}</span>
                        </Notification>
                    )}
                </Fade>
                <Fade enter={true} exit={true}>
                    {updatedMessage && (
                        <Notification
                            type={{ style: "success", icon: true }}
                            onClose={() => setUpdatedMessage(false)}
                        >
                            <span>{localizationService.toLanguageString('configuration.devices.updated_device', 'NOT_FOUND')}</span>
                        </Notification>
                    )}
                </Fade>
            </NotificationGroup>
            {devicesList.length > 0 &&
                <div
                    ref={scrollContainerRef}
                    className={`devices-bind-root ${deviceMacSelected ? "has-selection" : "no-selection"}`}
                >

                    {/* left panel */}
                    <div
                        className="devices-bind-left"
                    >
                        <div style={{ textAlign: 'center', marginBottom: '20px', justifyContent: 'space-between' }}>
                            <p
                                className="devices-bind-title"
                                style={{ display: 'inline-flex', textAlign: 'center', fontSize: '20px', color: COLORS.primary }}
                            >
                                {localizationService.toLanguageString('configuration.devices.devices_list', 'NOT_FOUND')} ({counterActiveDevices})
                            </p>
                        </div>

                        <div
                            ref={devicesListContainerRef}
                            style={{
                                flex: 1,
                                minHeight: 0,
                                padding: '8px',
                                width: '100%',
                                display: 'flex'
                            }}
                        >
                            <div
                                className="k-listview-item row p-2 align-middle devices-bind-toolbar-row"
                            >
                                <fieldset className="devices-bind-toolbar" style={{ display: 'flex' }}>
                                    <TextBox
                                        style={{
                                            border: `2px solid ${COLORS.borderMuted}`,
                                            boxShadow: 'inset 0px 0px 5px 0px rgba(0,0,0,0.13)',
                                            marginBottom: '10px',
                                        }}
                                        value={searchFilterValue}
                                        onChange={onFilterChange}
                                        placeholder={localizationService.toLanguageString('configuration.devices.search_device', 'NOT_FOUND')}
                                        prefix={() => (
                                            <SvgIcon icon={searchIcon} 
                                                svgStyle={{ margin: drawerExpanded ? '20px -5px 0px 5px' : '0px -5px 0px 5px' }} 
                                            />
                                        )}
                                    />
                                </fieldset>

                                <ListView
                                    className="k-listview-item row p-2 align-middle devices-bind-toolbar-row"
                                    data={listViewData.length > 0 ? listViewData : devicesList}
                                    item={DevicesRender}
                                    style={{
                                        background: 'transparent',
                                    }}
                                    footer={MyFooter}
                                />
                            </div>
                        </div>
                        {visible && (
                            <Dialog
                                className="confirm-action-dialog"
                                title={localizationService.toLanguageString('configuration.devices.remove_device', 'NOT_FOUND')}
                                onClose={toggleDialog}
                            >
                                <div className="dialog-actions">
                                    <button
                                        className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                                        onClick={toggleDialog}
                                    >
                                        {localizationService.toLanguageString('no', 'NOT_FOUND')}
                                    </button>
                                    <button
                                        className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                                        onClick={() => {
                                            if (visible) {
                                                toggleDialog()
                                                deleteDevice()
                                            }
                                        }}
                                    >
                                        {localizationService.toLanguageString('yes', 'NOT_FOUND')}
                                    </button>
                                </div>
                            </Dialog>
                        )}
                    </div>

                    {/* right panel */}
                    {deviceMacSelected !== '' &&
                        //<div className="flex-right-child-element" style={{ width: "35%", background: '#ffffff' }}>
                        <div
                            className="devices-bind-right"
                            style={{
                                overflowX: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 3,
                                alignItems: 'stretch'
                            }}
                        >
                            {/* mac + name */}
                            <div
                                className="k-listview-item row p-1 align-middle"
                                style={{ display: 'block', margin: 0, cursor: 'default' }}
                            >
                                <fieldset>
                                    <h1 className="k-h2" style={{ color: COLORS.primary }}>{localizationService.toLanguageString('configuration.devices.mac', 'NOT_FOUND')}</h1>
                                    <h5 className="k-h5" style={{ color: COLORS.primary }}>{deviceMacSelected != '' ? deviceMacSelected : localizationService.toLanguageString('configuration.devices.no_mac', 'NOT_FOUND')}</h5>

                                    <hr style={{ border: `1px solid ${COLORS.primary}`, width: '100%' }} />

                                    <div className="k-h5" style={{ color: COLORS.primary }}>{localizationService.toLanguageString('configuration.devices.name', 'NOT_FOUND')}</div>
                                    <TextBox
                                        style={{ color: COLORS.primary }}
                                        value={deviceNameSelected ?? ''}
                                        onChange={handleChangeDeviceName}
                                        placeholder={localizationService.toLanguageString('configuration.devices.enter_device_name', 'NOT_FOUND')}
                                        disabled={userType == 'StandardOnlyLocator'}
                                    />

                                    {/* device type */}
                                    {deviceTypeName != '' &&
                                        <div
                                            className="k-listview-item row p-1 align-middle"
                                            style={{ display: 'block', margin: 0, cursor: 'default', marginTop: '20px', height: '40px' }}
                                        >
                                            <div className="k-h5" style={{ display: 'flex', alignItems: 'baseline', color: '#018CA5' }}>
                                                <span style={{ marginRight: '10px' }}>{localizationService.toLanguageString('configuration.devices.device_type', 'Device type')}:</span>
                                                <p>{deviceTypeName ?? ''}</p>
                                            </div>
                                        </div>
                                    }

                                </fieldset>
                            </div>

                            <br />

                            {/* type */}
                            {/*<div className="k-listview-item row p-1 align-middle" style={{ cursor: 'default' }}>
                                <div className="col-2" >
                                    <Avatar type="image" style={{ background: COLORS.primary, border: 'none' }}>
                                        <SvgIcon icon={starIcon} style={{ verticalAlign: 'baseline', margin: '8px' }} />
                                    </Avatar>
                                </div>
                                <div className="col-9" >
                                    <h1
                                        style={{ fontSize: 16, color: COLORS.primary, verticalAlign: 'bottom', display: 'inline-flex' }}
                                        className="text-uppercase"
                                    >
                                        {deviceTypeName !== '' ? deviceTypeName : ''}
                                    </h1>
                                </div>
                            </div>*/}


                            {/* bindings */}
                            {devicesBinding.length > 0 ? (
                                <>
                                    <div
                                        className="k-listview-item row p-1 align-middle"
                                        style={{ display: 'block', margin: 0 }}
                                    >
                                        <p className="k-h5" style={{ color: COLORS.primary, margin: 0 }}>
                                            {localizationService.toLanguageString('configuration.devices.all_bindings', 'NOT_FOUND')}
                                        </p>
                                    </div>

                                    <Grid
                                        className="device-binding-grid"
                                        style={{
                                            margin: '.5rem',
                                            minHeight: '200px',
                                            height: '400px',
                                        }}
                                        data={devicesBinding}
                                    >
                                        <GridColumn
                                            field="serial"
                                            title={localizationService.toLanguageString('configuration.devices.name', 'NOT_FOUND')}
                                            width="150px"
                                        />
                                        <GridColumn
                                            field="startDate"
                                            title={localizationService.toLanguageString('configuration.devices.from', 'NOT_FOUND')}
                                            format='{0:dd/MM/yyyy HH:mm:ss}'
                                            width="150px"
                                            cells={{ data: CustomCell }}
                                        />
                                        <GridColumn
                                            field="endDate"
                                            title={localizationService.toLanguageString('configuration.devices.to', 'NOT_FOUND')}
                                            format='{0:dd/MM/yyyy HH:mm:ss}'
                                            width="150px"
                                            cells={{ data: CustomCell }}
                                        />
                                        <GridColumn
                                            field="closeBinding"
                                            title={localizationService.toLanguageString('configuration.devices.close_binding', 'NOT_FOUND')}
                                            width="140px"
                                            cells={{ data: CloseBindingIconGrid }}
                                            hidden={userType == 'StandardOnlyLocator'}
                                        />
                                    </Grid>
                                </>
                            ) : (
                                <div
                                    className="k-listview-item row p-1 align-middle"
                                    style={{ display: 'block', margin: 0 }}
                                >
                                    <p className="k-h5" style={{ color: COLORS.primary, margin: 0 }}>
                                        {localizationService.toLanguageString('configuration.devices.no_binding_found', 'No binding found')}
                                    </p>
                                </div>
                            )}

                            <br />

                            {/* save button */}
                            <div className="k-listview-item row p-4 devices-bind-save-row">
                                <Button
                                    type="button"
                                    themeColor={"primary"}
                                    className="save-devices-management-button"
                                    svgIcon={saveIcon}
                                    disabled={deviceNameSelected == '' || userType == 'StandardOnlyLocator'}
                                    onClick={(e: any) => {
                                        e.preventDefault();
                                        if (lastToBinding === '31/12/9999 00:00:00' && deviceInfoSelected) {
                                            closeDeviceBinding(deviceInfoSelected)
                                        } else {
                                            saveDevice(deviceMacSelected, deviceNameSelected)
                                        }
                                    }}
                                >
                                    {localizationService.toLanguageString('configuration.devices.save', 'NOT_FOUND')}
                                </Button>
                            </div>

                        </div>
                    }
                </div>
            }
        </>
    )
}
